import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AiGradientButton } from '../../shared';
import StockQtyStepper from './StockQtyStepper';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SaveIcon from '@mui/icons-material/Save';
import {
  createStockInventory,
  getStockInventoryById,
  getStockRawMaterials,
  getStockSuppliers,
  updateStockInventory,
  uploadStockInvoiceDocument,
} from '../../services/api';
import StockAiInvoiceOverlay from './StockAiInvoiceOverlay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import moment from 'moment';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { isNativeAppShell } from '../../utils/mobileView';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../../utils/nativeCameraCapture';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';


const DEFAULT_PRODUCT_OPTIONS = ['Rice', 'Flour', 'Oil', 'Sugar', 'Milk', 'Vegetables', 'Spices'];

const filterRawMaterialOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => option,
});
const UNIT_OPTIONS = [
  'kg',
  'g',
  'mg',
  'quintal',
  'ton',
  'lb',
  'oz',
  'ltr',
  'ml',
  'cl',
  'gallon',
  'pcs',
  'dozen',
  'pair',
  'packet',
  'box',
  'bag',
  'bottle',
  'can',
  'jar',
  'tin',
  'tub',
  'tray',
  'carton',
  'sachet',
  'pouch',
  'roll',
  'bundle',
  'sheet',
  'stick',
  'cup',
  'tbsp',
  'tsp',
];
const RAW_ITEM_GRID_TEMPLATE = '52px minmax(220px,2.3fr) minmax(95px,0.8fr) minmax(95px,0.8fr) minmax(110px,0.9fr) minmax(115px,0.95fr) minmax(88px,0.75fr) minmax(88px,0.75fr) minmax(88px,0.75fr) minmax(70px,0.55fr)';
/** Common GST slabs — applied as CGST + SGST (half each), IGST 0 (intra-state) */
const GST_APPLY_SLABS = [0, 5, 12, 18, 28];
const DATE_FORMAT = 'YYYY-MM-DD';

const newInvoiceDocId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const invoiceDraftSessionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Normalize API inventory into list of { id, url } for the form */
const invoiceAttachmentsFromInventory = (inventory) => {
  if (!inventory) return [];
  const raw = inventory.invoice_documents;
  let urls = [];
  if (Array.isArray(raw) && raw.length) {
    urls = raw.map((x) => (typeof x === 'string' ? x : x?.url)).filter(Boolean);
  }
  if (!urls.length && inventory.invoice_document_url) {
    urls = [inventory.invoice_document_url];
  }
  return urls.map((url) => ({ id: newInvoiceDocId(), url }));
};

/** Same labels as reservation ReviewStep payment section */
const STOCK_PAYMENT_METHODS = [
  'ONLINE',
  'CASH',
  'CARD',
  'BANK TRANSFER',
  'COMPANY SETTLEMENT',
  'UPI',
  'CREDIT SALE',
  'CHEQUE',
  'PAYMENT LINK',
];

const isInvoicePdfFile = (file) =>
  file?.type === 'application/pdf' ||
  file?.type === 'application/x-pdf' ||
  /\.pdf$/i.test(file?.name || '');

const isInvoiceImageFile = (file) =>
  Boolean(file?.type?.startsWith('image/')) ||
  /\.(jpe?g|png|gif|webp)$/i.test(file?.name || '');

/** Heuristic for preview: PDFs vs images in dotted upload box */
const isInvoicePdfUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.endsWith('.pdf');
};

const getInitialItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  product: '',
  name: '',
  quantity: '',
  unit: '',
  price: '',
  cgst: '',
  sgst: '',
  igst: '',
  description: '',
});

const newPaymentRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  method: 'CASH',
  amount: '',
});

const skeletonFieldSx = (isDark) => ({
  borderRadius: 1.2,
  bgcolor: isDark ? 'rgba(255,255,255,0.08)' : undefined,
});

function StockFieldSkeleton({ height = 40, isDark, sx = {} }) {
  return <Skeleton variant="rounded" height={height} sx={{ ...skeletonFieldSx(isDark), ...sx }} />;
}

function StockItemsTableSkeleton({ isDark, rows = 3 }) {
  return (
    <Stack spacing={1}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={`item-skeleton-${index}`}
          variant="rounded"
          height={72}
          sx={{ borderRadius: 1.5, ...skeletonFieldSx(isDark) }}
        />
      ))}
    </Stack>
  );
}

function StockPaymentDetailsSkeleton({ isDark }) {
  return (
    <Stack spacing={2}>
      <StockFieldSkeleton height={44} isDark={isDark} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <StockFieldSkeleton height={40} isDark={isDark} sx={{ flex: 1 }} />
        <StockFieldSkeleton height={40} isDark={isDark} sx={{ width: { sm: 148 } }} />
      </Stack>
      <StockFieldSkeleton height={128} isDark={isDark} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <StockFieldSkeleton height={40} isDark={isDark} sx={{ flex: 1 }} />
        <StockFieldSkeleton height={40} isDark={isDark} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}

function StockBillingSummarySkeleton({ isDark }) {
  return (
    <Stack spacing={1.2}>
      {[32, 40, 40, 40, 28, 28, 36].map((height, index) => (
        <StockFieldSkeleton key={`billing-skeleton-${index}`} height={height} isDark={isDark} />
      ))}
    </Stack>
  );
}

/** Load split payments from API or legacy single-method fields */
const mapApiPaymentsToRows = (inventory) => {
  const raw = inventory?.payment_entries;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((e) => ({
      id: `${Date.now()}-${Math.random()}`,
      method: String(e?.method || 'CASH').trim() || 'CASH',
      amount: e?.amount != null && e.amount !== '' ? String(e.amount) : '',
    }));
  }
  const pt = inventory?.payment_type;
  if (pt === 'partial_paid') {
    const amount =
      inventory.partial_paid_amount != null && inventory.partial_paid_amount !== ''
        ? String(inventory.partial_paid_amount)
        : '';
    const method =
      (inventory.payment_method && String(inventory.payment_method).trim()) || 'CASH';
    return [{ id: `${Date.now()}-${Math.random()}`, method, amount }];
  }
  if (pt === 'paid') {
    const amount =
      inventory.total_amount != null && inventory.total_amount !== ''
        ? String(inventory.total_amount)
        : '';
    const method =
      (inventory.payment_method && String(inventory.payment_method).trim()) || 'CASH';
    return [{ id: `${Date.now()}-${Math.random()}`, method, amount }];
  }
  return [newPaymentRow()];
};

const normalizeInvoiceDate = (value) => {
  if (!value) return '';
  const parsed = moment(value, [DATE_FORMAT, 'DD/MM/YYYY', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : '';
};

/** Label + control; stacks on xs when parent grid is single-column */
function RawMaterialField({ label, isDark, center, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: center ? 'center' : 'stretch' },
        gap: { xs: 0.35, md: 0 },
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          display: { xs: 'block', md: 'none' },
          fontWeight: 700,
          letterSpacing: 0.15,
          mb: 0.15,
          color: isDark ? '#cbd5e1' : '#475569',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          ...(center && {
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Text numeric input without native spinners; stacked MUI chevron steppers */
function NumericFieldWithMuiSteppers({
  value,
  onChange,
  disabled,
  step = 1,
  min,
  max,
  placeholder,
  isDark,
  borderColor,
  inputProps,
  sx,
  InputProps,
  ...rest
}) {
  const parseNum = () => {
    const s = String(value ?? '').trim();
    if (s === '') return null;
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const bump = (direction) => {
    let base = parseNum();
    if (base === null) base = 0;
    let next = base + direction * step;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    if (step < 1) {
      next = Math.round(next * 100) / 100;
    } else {
      next = Math.round(next);
    }
    onChange({ target: { value: String(next) } });
  };

  const n = parseNum();
  const effective = n ?? 0;
  const incDisabled =
    disabled || (max !== undefined && effective >= max);
  const decDisabled =
    disabled || (min !== undefined && effective <= min);

  const railBg = isDark ? 'rgba(255,255,255,0.08)' : '#e8edf4';
  const iconMuted = isDark ? '#94a3b8' : '#64748b';

  const stepperAdornment = (
    <InputAdornment
      position="end"
      sx={{
        m: 0,
        ml: '0px !important',
        mr: '0px !important',
        maxHeight: 'none',
        height: 'auto',
        alignSelf: 'stretch',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          justifyContent: 'stretch',
          borderLeft: `1px solid ${borderColor}`,
          borderRadius: '0 4px 4px 0',
          overflow: 'hidden',
          backgroundColor: railBg,
          width: 32,
          minWidth: 32,
          flexShrink: 0,
          mr: 0,
        }}
      >
        <IconButton
          type="button"
          size="small"
          disabled={incDisabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            bump(1);
          }}
          sx={{
            borderRadius: 0,
            py: 0,
            minHeight: 18,
            color: iconMuted,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(76,225,182,0.14)' : 'rgba(76,225,182,0.2)',
              color: isDark ? '#4ce1b6' : '#0d9488',
            },
          }}
          aria-label="Increase value"
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ height: '1px', backgroundColor: borderColor, opacity: 0.65 }} />
        <IconButton
          type="button"
          size="small"
          disabled={decDisabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            bump(-1);
          }}
          sx={{
            borderRadius: 0,
            py: 0,
            minHeight: 18,
            color: iconMuted,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(76,225,182,0.14)' : 'rgba(76,225,182,0.2)',
              color: isDark ? '#4ce1b6' : '#0d9488',
            },
          }}
          aria-label="Decrease value"
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </InputAdornment>
  );

  return (
    <TextField
      {...rest}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      size="small"
      fullWidth
      type="text"
      inputProps={{
        ...inputProps,
        inputMode: 'decimal',
        autoComplete: 'off',
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: 0,
        },
        '& input': {
          MozAppearance: 'textfield',
          textAlign: 'right',
          paddingRight: '10px !important',
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        ...sx,
      }}
      InputProps={{
        ...InputProps,
        sx: {
          pr: 0,
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 1.2,
          },
          ...InputProps?.sx,
        },
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {stepperAdornment}
          </>
        ),
      }}
    />
  );
}

const StockAddInventorySection = ({
  isDark,
  mode = 'add',
  inventoryId = null,
  onSaved,
  onCancel,
}) => {
  const { can } = usePermission();
  const canManageAiInventory = can(PERMISSIONS.AI_INVENTORY_MANAGE);
  const isReadOnly = mode === 'view';
  const [suppliers, setSuppliers] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(
    () => (mode === 'edit' || mode === 'view') && Boolean(inventoryId)
  );
  const isFormLoading = loadingMasters || loadingInventory;
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    purchase_from: 'supplier',
    purchase_for: 'hotel',
    supplier_id: '',
    invoice_date: '',
    invoice_number: '',
    gst_number: '',
    payment_type: 'paid',
    payment_entries: [newPaymentRow()],
    invoice_documents: [],
    discount: '',
    delivery_charges: '',
    other_taxes: '',
    invoice_level_tax_rate: '0',
  });
  const [items, setItems] = useState([getInitialItem()]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [moreActionMenuAnchor, setMoreActionMenuAnchor] = useState(null);
  const [applyTaxMenuAnchor, setApplyTaxMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
  const [invoiceDocUploading, setInvoiceDocUploading] = useState(false);
  const invoiceDocInputRef = useRef(null);
  const invoiceCameraVideoRef = useRef(null);
  const invoiceCameraCanvasRef = useRef(null);
  const [invoiceCameraOpen, setInvoiceCameraOpen] = useState(false);
  const [invoiceCameraStream, setInvoiceCameraStream] = useState(null);
  const [invoiceCameraUploadMode, setInvoiceCameraUploadMode] = useState(false);
  const invoiceDraftIdRef = useRef(invoiceDraftSessionId());
  const [invoicePreviewFailedById, setInvoicePreviewFailedById] = useState({});
  const theme = useTheme();

  const sectionBg = isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff';
  const borderColor = isDark ? alpha('#ffffff', 0.08) : '#e0e0e0';
  /** Stronger than section borders — used for qty stepper / numeric rails so controls read on white */
  const inputOutlineColor = isDark ? '#64748b' : '#94a3b8';
  const itemCardBg = isDark ? '#23232a' : '#f8fafc';
  const naturalInputBg = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc';
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  useEffect(() => {
    let cancelled = false;

    const loadMastersOnly = async () => {
      setLoadingMasters(true);
      try {
        const [supRes, rawRes] = await Promise.all([getStockSuppliers(), getStockRawMaterials()]);
        if (cancelled) return;
        setSuppliers(supRes?.suppliers || []);
        setRawMaterials(rawRes?.raw_materials || []);
      } catch (error) {
        if (!cancelled) {
          showMessage('error', error?.userMessage || 'Failed to load suppliers or raw materials');
        }
      } finally {
        if (!cancelled) setLoadingMasters(false);
      }
    };

    if (mode === 'add') {
      setFormData((prev) => {
        const rows =
          Array.isArray(prev.payment_entries) && prev.payment_entries.length > 0
            ? prev.payment_entries
            : [newPaymentRow()];
        const payment_entries =
          prev.payment_type !== 'unpaid'
            ? rows.map((r, i) =>
                i === 0 && !String(r.method || '').trim() ? { ...r, method: 'CASH' } : r
              )
            : rows;
        return {
          ...prev,
          invoice_date: prev.invoice_date || moment().format(DATE_FORMAT),
          payment_entries,
          invoice_documents: [],
        };
      });
      setItems([getInitialItem()]);
      setSelectedItemIds([]);
      loadMastersOnly();
      return () => {
        cancelled = true;
      };
    }

    if (!inventoryId) {
      return () => {
        cancelled = true;
      };
    }

    setLoadingMasters(true);
    setLoadingInventory(true);
    (async () => {
      try {
        const [supRes, rawRes, invRes] = await Promise.all([
          getStockSuppliers(),
          getStockRawMaterials(),
          getStockInventoryById(inventoryId),
        ]);
        if (cancelled) return;
        setSuppliers(supRes?.suppliers || []);
        setRawMaterials(rawRes?.raw_materials || []);
        setLoadingMasters(false);
        const inventory = invRes?.inventory;
        if (!inventory) return;
        setFormData({
          purchase_from: inventory.purchase_from || 'supplier',
          purchase_for: inventory.purchase_for || 'hotel',
          supplier_id: inventory.supplier_id || '',
          invoice_date: normalizeInvoiceDate(inventory.invoice_date),
          invoice_number: inventory.invoice_number || '',
          gst_number: inventory.gst_number || '',
          payment_type: inventory.payment_type || 'paid',
          payment_entries: mapApiPaymentsToRows(inventory),
          invoice_documents: invoiceAttachmentsFromInventory(inventory),
          discount: inventory.discount || '',
          delivery_charges: inventory.delivery_charges || '',
          other_taxes: inventory.other_taxes || '',
          invoice_level_tax_rate: String(inventory.invoice_level_tax_rate || 0),
        });
        setItems(
          (inventory.items || []).map((item) => ({
            id: item.id || `${Date.now()}-${Math.random()}`,
            product: item.product || item.name || '',
            name: item.name || item.product || '',
            quantity: item.quantity || '',
            unit: item.unit || '',
            price: item.price || '',
            cgst: item.cgst || '',
            sgst: item.sgst || '',
            igst: item.igst || '',
            description: item.description || '',
          }))
        );
      } catch (error) {
        if (!cancelled) {
          showMessage('error', error?.userMessage || 'Failed to load inventory details');
        }
      } finally {
        if (!cancelled) {
          setLoadingMasters(false);
          setLoadingInventory(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inventoryId, mode]);

  const rawMaterialProductOptions = useMemo(() => {
    const namesFromMaster = rawMaterials
      .filter((material) => material?.is_active !== false)
      .map((material) => String(material?.name || '').trim())
      .filter(Boolean);
    if (namesFromMaster.length) {
      return [...new Set(namesFromMaster)];
    }
    return DEFAULT_PRODUCT_OPTIONS;
  }, [rawMaterials]);

  const rawMaterialUnitByName = useMemo(() => {
    const unitMap = {};
    rawMaterials.forEach((material) => {
      const name = String(material?.name || '').trim().toLowerCase();
      if (!name) return;
      unitMap[name] = String(material?.unit || '').trim();
    });
    return unitMap;
  }, [rawMaterials]);

  useEffect(() => {
    setSelectedItemIds((prev) => prev.filter((id) => items.some((item) => item.id === id)));
  }, [items]);

  useEffect(() => {
    return () => {
      if (invoiceCameraStream) {
        invoiceCameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [invoiceCameraStream]);

  useEffect(() => {
    setInvoicePreviewFailedById({});
  }, [formData.invoice_documents]);

  const getItemAmount = (item) => {
    const quantity = Number(item.quantity || 0);
    const price = Number(item.price || 0);
    return quantity * price;
  };

  const getItemTaxAmount = (item) => {
    const amount = getItemAmount(item);
    const taxRate = Number(item.cgst || 0) + Number(item.sgst || 0) + Number(item.igst || 0);
    return (amount * taxRate) / 100;
  };

  const summary = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + getItemAmount(item), 0);
    const cgstTax = items.reduce((sum, item) => sum + ((getItemAmount(item) * Number(item.cgst || 0)) / 100), 0);
    const sgstTax = items.reduce((sum, item) => sum + ((getItemAmount(item) * Number(item.sgst || 0)) / 100), 0);
    const igstTax = items.reduce((sum, item) => sum + ((getItemAmount(item) * Number(item.igst || 0)) / 100), 0);
    const itemTax = cgstTax + sgstTax + igstTax;
    const invoiceLevelTaxRate = Number(formData.invoice_level_tax_rate || 0);
    const invoiceLevelTaxAmount = (subtotal * invoiceLevelTaxRate) / 100;
    const otherTaxes = Number(formData.other_taxes || 0);
    const totalTax = itemTax + invoiceLevelTaxAmount + otherTaxes;
    const discount = Number(formData.discount || 0);
    const otherCharges = Number(formData.delivery_charges || 0);
    const taxableSubtotal = Math.max(0, subtotal - discount + otherCharges);
    const total = Math.max(0, subtotal + totalTax + otherCharges - discount);
    return {
      subtotal,
      taxableSubtotal,
      cgstTax,
      sgstTax,
      igstTax,
      itemTax,
      invoiceLevelTaxRate,
      invoiceLevelTaxAmount,
      otherTaxes,
      totalTax,
      discount,
      otherCharges,
      total,
    };
  }, [items, formData.delivery_charges, formData.discount, formData.invoice_level_tax_rate, formData.other_taxes]);

  const totalPaidFromEntries = useMemo(
    () =>
      (formData.payment_entries || []).reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [formData.payment_entries]
  );

  const amountDueFromPayments = useMemo(
    () => Math.max(0, summary.total - totalPaidFromEntries),
    [summary.total, totalPaidFromEntries]
  );

  const PAID_TOTAL_TOLERANCE = 0.051;

  /** When Paid + single payment row, keep amount aligned with grand total. */
  useEffect(() => {
    if (isReadOnly || isFormLoading) return;
    setFormData((prev) => {
      if (prev.payment_type !== 'paid') return prev;
      const entries = prev.payment_entries || [];
      if (entries.length !== 1) return prev;
      if (summary.total <= 0) return prev;
      const target = Number(summary.total.toFixed(2));
      const current = Number(entries[0].amount || 0);
      if (Math.abs(current - target) < PAID_TOTAL_TOLERANCE) return prev;
      return {
        ...prev,
        payment_entries: [{ ...entries[0], amount: target.toFixed(2) }],
      };
    });
  }, [isReadOnly, isFormLoading, summary.total]);

  /** Partial + payments covering grand total → treat as paid (matches billing summary “due”) */
  useEffect(() => {
    if (isReadOnly || isFormLoading) return;
    if (formData.payment_type !== 'partial_paid') return;
    if (summary.total <= 0) return;
    if (amountDueFromPayments > PAID_TOTAL_TOLERANCE) return;
    setFormData((prev) =>
      prev.payment_type === 'partial_paid' ? { ...prev, payment_type: 'paid' } : prev
    );
  }, [
    isReadOnly,
    isFormLoading,
    formData.payment_type,
    amountDueFromPayments,
    summary.total,
  ]);

  const hasItems = items.length > 0;
  const hasValidItems = items.every(
    (item) => (item.name || item.product) && Number(item.quantity || 0) > 0
  );
  const needsPaymentLines =
    formData.payment_type === 'paid' || formData.payment_type === 'partial_paid';

  const paymentRowsWithAmount = (formData.payment_entries || []).filter(
    (r) => Number(r.amount || 0) > 0 && String(r.method || '').trim()
  );

  const paidTotalsMatch =
    formData.payment_type !== 'paid' ||
    Math.abs(totalPaidFromEntries - summary.total) < PAID_TOTAL_TOLERANCE;

  const partialTotalsValid =
    formData.payment_type !== 'partial_paid' ||
    (totalPaidFromEntries > 0 && totalPaidFromEntries <= summary.total + 1e-9);

  const paymentLinesValid =
    !needsPaymentLines ||
    (paymentRowsWithAmount.length > 0 && paidTotalsMatch && partialTotalsValid);

  const isFormValid =
    Boolean(formData.supplier_id) &&
    Boolean(formData.invoice_date) &&
    paymentLinesValid &&
    hasItems &&
    hasValidItems;

  useEffect(() => {
    if (isFormValid) setSubmitAttempted(false);
  }, [isFormValid]);

  const paymentValidationMessage = useMemo(() => {
    if (!needsPaymentLines || paymentLinesValid) return '';
    if (paymentRowsWithAmount.length === 0) {
      return 'Add at least one payment with amount greater than 0.';
    }
    if (formData.payment_type === 'paid' && !paidTotalsMatch) {
      return `Total paid must equal grand total (${summary.total.toFixed(2)}).`;
    }
    if (formData.payment_type === 'partial_paid' && !partialTotalsValid) {
      return `Partial payment must be greater than 0 and not exceed grand total (${summary.total.toFixed(2)}).`;
    }
    return 'Adjust payment lines to match the billing summary.';
  }, [
    needsPaymentLines,
    paymentLinesValid,
    paymentRowsWithAmount.length,
    formData.payment_type,
    paidTotalsMatch,
    partialTotalsValid,
    summary.total,
  ]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updatePaymentRow = (rowId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      payment_entries: (prev.payment_entries || []).map((r) =>
        r.id === rowId ? { ...r, [field]: value } : r
      ),
    }));
  };

  const addPaymentRow = () => {
    setFormData((prev) => ({
      ...prev,
      payment_entries: [...(prev.payment_entries || []), newPaymentRow()],
    }));
  };

  const removePaymentRow = (rowId) => {
    setFormData((prev) => {
      const list = prev.payment_entries || [];
      if (list.length <= 1) return prev;
      return { ...prev, payment_entries: list.filter((r) => r.id !== rowId) };
    });
  };

  const handlePaymentTypeChange = (newValue) => {
    if (newValue === null || isReadOnly || isFormLoading) return;
    setFormData((prev) => {
      if (newValue === 'unpaid') {
        return { ...prev, payment_type: 'unpaid', payment_entries: [newPaymentRow()] };
      }
      const entries =
        prev.payment_entries?.length >= 1 ? [...prev.payment_entries] : [newPaymentRow()];
      if (newValue === 'paid' && entries.length === 1 && summary.total > 0) {
        entries[0] = {
          ...entries[0],
          amount: Number(summary.total.toFixed(2)).toFixed(2),
        };
      }
      return {
        ...prev,
        payment_type: newValue,
        payment_entries: entries,
      };
    });
  };

  const removeInvoiceDocument = (docId) => {
    setFormData((prev) => ({
      ...prev,
      invoice_documents: (prev.invoice_documents || []).filter((d) => d.id !== docId),
    }));
  };

  const uploadInvoiceMediaFile = async (file) => {
    if (!file || isReadOnly) return;
    if (!isInvoicePdfFile(file) && !isInvoiceImageFile(file)) {
      showMessage('error', 'Please upload a PDF or image (JPEG, PNG, GIF, WebP).');
      return;
    }
    setInvoiceDocUploading(true);
    try {
      const res = await uploadStockInvoiceDocument(file, {
        stockInventoryId: inventoryId || null,
        draftId: inventoryId ? null : invoiceDraftIdRef.current,
      });
      const url = res?.file?.url;
      if (!url) {
        throw new Error(res?.message || 'Upload did not return a file URL');
      }
      setFormData((prev) => ({
        ...prev,
        invoice_documents: [...(prev.invoice_documents || []), { id: newInvoiceDocId(), url }],
      }));
      showMessage('success', 'Invoice uploaded.');
    } catch (error) {
      const msg =
        typeof error === 'string'
          ? error
          : error?.error ||
            error?.message ||
            (typeof error?.detail === 'string' ? error.detail : null) ||
            'Failed to upload invoice';
      showMessage('error', msg);
    } finally {
      setInvoiceDocUploading(false);
    }
  };

  const handleInvoiceDocumentFile = async (event) => {
    const files = Array.from(event.target.files || []);
    const input = event.target;
    if (input) input.value = '';
    if (!files.length) return;
    for (const file of files) {
      await uploadInvoiceMediaFile(file);
    }
  };

  const stopInvoiceCamera = () => {
    if (invoiceCameraStream) {
      invoiceCameraStream.getTracks().forEach((track) => track.stop());
      setInvoiceCameraStream(null);
    }
    setInvoiceCameraOpen(false);
  };

  const startInvoiceCamera = async () => {
    if (isNativeAppShell()) {
      try {
        const dataUrl = await captureNativeCameraDataUrl();
        if (!dataUrl) return;
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `invoice-photo-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' });
        await uploadInvoiceMediaFile(file);
      } catch (error) {
        const message = getNativeCameraErrorMessage(error);
        if (message) {
          showMessage('error', message);
        }
      }
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showMessage(
        'error',
        'Camera is not available in this browser. Use Upload to choose an image or PDF from files.'
      );
      return;
    }
    try {
      if (invoiceCameraStream) {
        invoiceCameraStream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setInvoiceCameraStream(mediaStream);
      setInvoiceCameraOpen(true);
      setTimeout(() => {
        if (invoiceCameraVideoRef.current) {
          invoiceCameraVideoRef.current.srcObject = mediaStream;
          invoiceCameraVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch {
      showMessage(
        'error',
        'Camera access was denied or unavailable. Allow camera permission or upload from files.'
      );
    }
  };

  const captureInvoiceDocument = () => {
    const video = invoiceCameraVideoRef.current;
    const canvas = invoiceCameraCanvasRef.current;
    if (!video || !canvas) return;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    canvas.toBlob(
      async (blob) => {
        stopInvoiceCamera();
        if (!blob) return;
        const file = new File([blob], `invoice-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        await uploadInvoiceMediaFile(file);
      },
      'image/jpeg',
      0.85
    );
  };

  const handleInvoiceDocumentZoneClick = () => {
    if (isReadOnly || isFormLoading || invoiceDocUploading) return;
    if (invoiceCameraUploadMode) {
      startInvoiceCamera();
    } else {
      invoiceDocInputRef.current?.click();
    }
  };

  const handleItemChange = (rowId, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== rowId) return item;
        const nextItem = { ...item, [field]: value };
        if (field === 'product') {
          nextItem.name = value;
          const mappedUnit = rawMaterialUnitByName[String(value || '').trim().toLowerCase()];
          if (mappedUnit && !String(nextItem.unit || '').trim()) {
            nextItem.unit = mappedUnit;
          }
        }
        return nextItem;
      })
    );
  };

  const addItemRow = () => setItems((prev) => [...prev, getInitialItem()]);

  const removeItemRow = (rowId) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((item) => item.id !== rowId)));
  };

  const handleToggleRowSelection = (rowId) => {
    setSelectedItemIds((prev) => (
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    ));
  };

  const handleToggleAllRows = (checked) => {
    if (!checked) {
      setSelectedItemIds([]);
      return;
    }
    setSelectedItemIds(items.map((item) => item.id));
  };

  const removeSelectedRows = () => {
    if (!selectedItemIds.length) return;
    setItems((prev) => {
      const remaining = prev.filter((item) => !selectedItemIds.includes(item.id));
      return remaining.length ? remaining : [getInitialItem()];
    });
    setSelectedItemIds([]);
  };

  const formatGstHalfPercent = (totalRate) => {
    const h = totalRate / 2;
    if (Number.isInteger(h)) return String(h);
    return String(Number(h.toFixed(2)));
  };

  /** Intra-state: CGST + SGST = slab (half each), IGST 0. Selected rows only (use checkboxes). */
  const applyGstSlabToRows = (totalRate) => {
    if (isFormLoading) return;
    if (!selectedItemIds.length) {
      showMessage('warning', 'Select one or more rows first.');
      setApplyTaxMenuAnchor(null);
      return;
    }
    const half = formatGstHalfPercent(totalRate);
    setItems((prev) =>
      prev.map((item) => {
        if (!selectedItemIds.includes(item.id)) return item;
        if (totalRate === 0) {
          return { ...item, cgst: '0', sgst: '0', igst: '0' };
        }
        return {
          ...item,
          cgst: half,
          sgst: half,
          igst: '0',
        };
      })
    );
    setApplyTaxMenuAnchor(null);
    const where = `${selectedItemIds.length} selected row(s)`;
    showMessage(
      'success',
      totalRate === 0
        ? `Cleared CGST, SGST, and IGST on ${where}.`
        : `Applied ${totalRate}% GST (CGST ${half}% + SGST ${half}%, IGST 0%) on ${where}.`
    );
  };

  const handleMoreAction = (action) => {
    setMoreActionMenuAnchor(null);
    if (action === 'duplicate_last') {
      const lastItem = items[items.length - 1];
      if (!lastItem) return;
      setItems((prev) => [...prev, { ...lastItem, id: `${Date.now()}-${Math.random()}` }]);
      showMessage('success', 'Last row duplicated');
      return;
    }

    if (action === 'fill_units') {
      setItems((prev) => prev.map((item) => ({ ...item, unit: item.unit || 'kg' })));
      showMessage('success', 'Filled empty unit with kg');
      return;
    }

    if (action === 'reset_taxes') {
      setItems((prev) => prev.map((item) => ({ ...item, cgst: '', sgst: '', igst: '' })));
      handleFormChange('invoice_level_tax_rate', '0');
      handleFormChange('other_taxes', '');
      showMessage('success', 'All tax values reset');
      return;
    }

    if (action === 'remove_selected') {
      if (!selectedItemIds.length) {
        showMessage('warning', 'Select rows first');
        return;
      }
      removeSelectedRows();
      showMessage('success', 'Selected rows removed');
      return;
    }

    if (action === 'clear_all_rows') {
      setItems([getInitialItem()]);
      setSelectedItemIds([]);
      showMessage('success', 'Rows reset to a single empty row');
    }
  };

  const normalizeText = (value) => String(value || '').trim().toLowerCase();

  /** Strip legal suffix noise so “ABC Pvt Ltd” matches “ABC” */
  const LEGAL_SUFFIX_RE =
    /\b(limited|ltd\.?|pvt\.?\s*ltd\.?|private|inc\.?|corp\.?|corporation|company|co\.)\b/gi;

  const normalizeSupplierTokens = (s) =>
    normalizeText(s).replace(LEGAL_SUFFIX_RE, ' ').replace(/[.'&,]/g, ' ').replace(/\s+/g, ' ').trim();

  /** Score 0–100; best supplier wins so similar names auto-select */
  const supplierSimilarityScore = (extractedRaw, supplier) => {
    const ex = normalizeSupplierTokens(extractedRaw);
    if (!ex) return 0;

    const candidates = [
      normalizeSupplierTokens(supplier?.name || ''),
      normalizeSupplierTokens(supplier?.company || ''),
    ].filter(Boolean);

    let best = 0;
    for (const cand of candidates) {
      if (!cand) continue;
      if (ex === cand) {
        best = 100;
        break;
      }
      if (cand.includes(ex) || ex.includes(cand)) {
        best = Math.max(best, 88);
        continue;
      }
      const eWords = ex.split(' ').filter((w) => w.length > 1);
      const cWords = cand.split(' ');
      if (!eWords.length) continue;
      let hits = 0;
      for (const w of eWords) {
        if (cWords.some((cw) => cw === w || cw.includes(w) || w.includes(cw))) hits += 1;
      }
      const ratio = hits / eWords.length;
      if (ratio >= 0.51) {
        best = Math.max(best, 55 + Math.round(35 * ratio));
      }
    }
    return best;
  };

  const findSupplierIdByExtractedName = (supplierName) => {
    const trimmed = String(supplierName || '').trim();
    if (!trimmed || !suppliers.length) return null;

    let bestId = null;
    let bestScore = 0;
    for (const supplier of suppliers) {
      const score = supplierSimilarityScore(trimmed, supplier);
      if (score > bestScore) {
        bestScore = score;
        bestId = supplier.id;
      }
    }
    if (bestScore >= 52) return bestId;
    return null;
  };

  const handleApplyExtractedInvoice = (extractedData) => {
    const supplierId = findSupplierIdByExtractedName(extractedData?.supplier_name);
    const extractedItems = Array.isArray(extractedData?.items) ? extractedData.items : [];

    const nextItems = extractedItems.length
      ? extractedItems.map((item) => {
          let qty = Number(item?.quantity);
          const rateN = Number(item?.rate);
          const priceN = Number(item?.price ?? item?.unit_price);
          const amountRaw = Number(item?.amount);
          // Rate OR Price → Stock Price. Prefer rate when both exist.
          let price =
            Number.isFinite(rateN) && rateN > 0
              ? rateN
              : Number.isFinite(priceN) && priceN > 0
                ? priceN
                : NaN;
          // Guard only: Amount was wrongly copied into rate/price.
          if (
            Number.isFinite(qty) &&
            qty > 1 &&
            Number.isFinite(amountRaw) &&
            amountRaw > 0 &&
            Number.isFinite(price) &&
            Math.abs(price - amountRaw) <= Math.max(0.5, amountRaw * 0.02)
          ) {
            price = Number((amountRaw / qty).toFixed(4));
          }
          // Repair qty when Amount ÷ Price is a clean whole number (e.g. Aatta 50→160).
          if (
            Number.isFinite(amountRaw) &&
            amountRaw > 0 &&
            Number.isFinite(price) &&
            price > 0 &&
            (!Number.isFinite(qty) ||
              qty <= 0 ||
              Math.abs(qty * price - amountRaw) > Math.max(1, amountRaw * 0.02))
          ) {
            const recovered = amountRaw / price;
            const whole = Math.round(recovered);
            if (Math.abs(recovered - whole) <= 0.08 && whole >= 1 && whole <= 10000) {
              qty = whole;
            }
          }
          const gstRateRaw = Number(item?.gst_rate);
          const gstSlabs = [0, 5, 12, 18, 28, 40];
          let gstRate = Number.isFinite(gstRateRaw) ? gstRateRaw : NaN;
          // Model may return half-rate (2.5 / 9 / 20) — convert to full GST Rate.
          if (gstRate === 2.5) gstRate = 5;
          else if (gstRate === 9) gstRate = 18;
          else if (gstRate === 20) gstRate = 40;
          else if (Number.isFinite(gstRate)) {
            const nearest = gstSlabs.reduce((best, s) =>
              Math.abs(s - gstRate) < Math.abs(best - gstRate) ? s : best
            );
            if (Math.abs(nearest - gstRate) <= 1.5) gstRate = nearest;
          }
          const halfGst =
            Number.isFinite(gstRate) && gstRate > 0
              ? Number((gstRate / 2).toFixed(3))
              : '';
          const cgstVal = item?.cgst;
          const sgstVal = item?.sgst;
          const igstVal = item?.igst;
          return {
            id: `${Date.now()}-${Math.random()}`,
            product: String(item?.name || '').trim(),
            name: String(item?.name || '').trim(),
            quantity: Number.isFinite(qty) && qty > 0 ? qty : item?.quantity ?? '',
            unit: String(item?.unit || '').trim(),
            price: Number.isFinite(price) && price > 0 ? price : item?.price ?? '',
            cgst:
              cgstVal !== undefined && cgstVal !== null && cgstVal !== '' && !Number.isFinite(gstRate)
                ? cgstVal
                : halfGst === ''
                  ? cgstVal ?? ''
                  : halfGst,
            sgst:
              sgstVal !== undefined && sgstVal !== null && sgstVal !== '' && !Number.isFinite(gstRate)
                ? sgstVal
                : halfGst === ''
                  ? sgstVal ?? ''
                  : halfGst,
            igst:
              Number.isFinite(gstRate) && gstRate > 0
                ? 0
                : igstVal !== undefined && igstVal !== null && igstVal !== ''
                  ? igstVal
                  : '',
            description: '',
          };
        })
      : [getInitialItem()];

    setFormData((prev) => ({
      ...prev,
      purchase_from: 'supplier',
      supplier_id: supplierId || prev.supplier_id,
      invoice_number: extractedData?.invoice_number || prev.invoice_number,
      invoice_date: normalizeInvoiceDate(extractedData?.invoice_date) || prev.invoice_date,
    }));
    setItems(nextItems);
    setSelectedItemIds([]);
    setInvoiceUploadOpen(false);

    if (extractedData?.supplier_name && !supplierId) {
      showMessage(
        'warning',
        `Invoice extracted. Supplier "${extractedData.supplier_name}" not found in list; please select manually.`
      );
      return;
    }
    showMessage('success', 'Invoice extracted and form autofilled. Review before saving.');
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    if (!isFormValid) {
      setSubmitAttempted(true);
      return;
    }
    setSaving(true);
    const cleanedPaymentEntries = (formData.payment_entries || [])
      .filter((r) => Number(r.amount || 0) > 0 && String(r.method || '').trim())
      .map((r) => ({
        method: String(r.method).trim(),
        amount: Number(r.amount || 0),
      }));

    const payload = {
      purchase_from: formData.purchase_from,
      purchase_for: formData.purchase_for,
      supplier_id: Number(formData.supplier_id),
      invoice_date: formData.invoice_date,
      invoice_number: formData.invoice_number,
      gst_number: formData.gst_number,
      payment_type: formData.payment_type,
      payment_entries: formData.payment_type === 'unpaid' ? [] : cleanedPaymentEntries,
      invoice_documents: (formData.invoice_documents || []).map((d) => d.url).filter(Boolean),
      invoice_document_url: (formData.invoice_documents || [])[0]?.url || null,
      discount: Number(formData.discount || 0),
      delivery_charges: Number(formData.delivery_charges || 0),
      items: items.map((item) => ({
        product: item.product,
        name: item.name || item.product,
        quantity: Number(item.quantity || 0),
        unit: item.unit,
        price: Number(item.price || 0),
        cgst: Number(item.cgst || 0),
        sgst: Number(item.sgst || 0),
        igst: Number(item.igst || 0),
        description: item.description,
      })),
    };

    try {
      const response =
        mode === 'edit' && inventoryId
          ? await updateStockInventory(inventoryId, payload)
          : await createStockInventory(payload);
      showMessage('success', response?.message || 'Stock saved successfully');
      if (onSaved) onSaved(response?.inventory);
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  const areAllRowsSelected = Boolean(items.length) && selectedItemIds.length === items.length;
  const hasPartialSelection = selectedItemIds.length > 0 && !areAllRowsSelected;

  const invoiceDateValue = formData.invoice_date ? moment(formData.invoice_date, DATE_FORMAT, true) : null;
  const selectedSupplierOption = useMemo(
    () => suppliers.find((supplier) => String(supplier.id) === String(formData.supplier_id)) || null,
    [formData.supplier_id, suppliers]
  );
  const supplierFieldError = submitAttempted && !formData.supplier_id;
  const invoiceDateFieldError = submitAttempted && !formData.invoice_date;
  const paymentSectionError = submitAttempted && needsPaymentLines && !paymentLinesValid;
  const itemsTableError = submitAttempted && !hasValidItems;
  const getChoiceChipSx = (isSelected) => ({
    borderRadius: 1.1,
    fontWeight: 600,
    borderColor: isSelected ? '#4ce1b6' : (isDark ? '#3f4658' : '#cfd8e3'),
    backgroundColor: isSelected ? '#4ce1b6' : 'transparent',
    color: isSelected ? '#06291f' : (isDark ? '#d1d5db' : '#475569'),
    '&:hover': {
      backgroundColor: isSelected ? '#4ce1b6' : (isDark ? 'rgba(76,225,182,0.14)' : 'rgba(76,225,182,0.12)'),
      borderColor: '#4ce1b6',
    },
    '& .MuiChip-label': {
      px: 1.35,
    },
  });
  const accent = '#4ce1b6';
  /** Shared segmented toggle (Hotel/Restaurant); compact width */
  const segmentedToggleGroupSx = {
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
    backgroundColor: 'transparent',
    '& .MuiToggleButtonGroup-grouped': {
      margin: 0,
      border: 0,
      borderRadius: 0,
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
      borderLeft: `1px solid ${borderColor}`,
    },
    '& .MuiToggleButton-root': {
      px: 2.25,
      py: 0.85,
      minWidth: 148,
      height: 40,
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0.15,
      gap: 0.75,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      boxShadow: 'none',
      color: isDark ? '#9ca3af' : '#64748b',
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        backgroundImage: 'none',
      },
      '&.Mui-selected': {
        color: `${accent} !important`,
        backgroundColor: `${isDark ? 'rgba(76,225,182,0.14)' : 'rgba(76,225,182,0.12)'} !important`,
        backgroundImage: 'none',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: `${isDark ? 'rgba(76,225,182,0.2)' : 'rgba(76,225,182,0.16)'} !important`,
          backgroundImage: 'none',
        },
      },
      '& .MuiSvgIcon-root': {
        fontSize: 20,
      },
      '&.Mui-selected .MuiSvgIcon-root': {
        color: `${accent} !important`,
      },
    },
  };
  /** Full-width row (Paid/Unpaid): fills grid column; equal segments */
  const segmentedToggleGroupFullWidthSx = {
    ...segmentedToggleGroupSx,
    width: '100%',
    alignSelf: 'stretch',
    '& .MuiToggleButton-root': {
      ...segmentedToggleGroupSx['& .MuiToggleButton-root'],
      flex: 1,
      minWidth: 0,
      maxWidth: 'none',
    },
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ position: 'relative' }}>
      {isFormLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            height: 2,
            borderRadius: 1,
          }}
        />
      )}
      <Grid
        container
        spacing={2}
        sx={{
          ...(invoiceUploadOpen
            ? {
                filter: 'blur(10px)',
                opacity: 0.55,
                transition: 'filter 0.35s ease, opacity 0.35s ease',
                pointerEvents: 'none',
                userSelect: 'none',
              }
            : {}),
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.2,
            backgroundColor: naturalInputBg,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            /* Visible ring — keep fieldset border transparent (not none) so notched legend + label gap render */
            boxShadow: isDark
              ? '0 0 0 1px rgba(100,116,139,0.5)'
              : '0 0 0 1px rgba(148,163,184,0.95)',
            '& fieldset': {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'transparent',
            },
            '&:hover': {
              boxShadow: isDark
                ? '0 0 0 1px rgba(148,163,184,0.65)'
                : '0 0 0 1px rgba(100,116,139,0.9)',
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.045)' : '#ffffff',
              boxShadow: isDark
                ? '0 0 0 2px rgba(76,225,182,0.45)'
                : '0 0 0 2px rgba(20,184,166,0.5)',
            },
            '&.Mui-error fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-error': {
              boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
            },
            '&.Mui-focused.Mui-error': {
              boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
            },
          },
          /* Shrink label masks the box-shadow ring behind text (fieldset stays transparent for notch) */
          '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            backgroundColor: naturalInputBg,
            px: 0.6,
            ml: -0.3,
            borderRadius: 0.5,
          },
          '& .MuiFormControl-root.Mui-focused .MuiInputLabel-root.MuiInputLabel-shrink': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.045)' : '#ffffff',
          },
          '& .MuiFormControl-root.Mui-error .MuiInputLabel-root.MuiInputLabel-shrink': {
            backgroundColor: naturalInputBg,
          },
          '& .MuiInputBase-input': {
            color: isDark ? '#e5e7eb' : '#111827',
          },
          '& .MuiInputBase-input::placeholder': {
            color: isDark ? '#8891a1' : '#94a3b8',
            opacity: 1,
          },
          '& .MuiInputLabel-root': {
            color: isDark ? '#9ca3af' : '#6b7280',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#4ce1b6',
          },
          '& .MuiSvgIcon-root': {
            color: isDark ? '#aeb4c2' : '#64748b',
          },
        }}
      >
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: sectionBg }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'stretch',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'stretch',
                  gap: 1.5,
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <Box sx={{ flex: { xs: 'none', md: '2 1 0%' }, minWidth: 0, width: '100%' }}>
                  {loadingMasters ? (
                    <StockFieldSkeleton height={40} isDark={isDark} />
                  ) : (
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={suppliers}
                    value={selectedSupplierOption}
                    onChange={(_event, newValue) => handleFormChange('supplier_id', newValue?.id || '')}
                    disabled={isReadOnly || loadingMasters}
                    getOptionLabel={(option) => String(option?.name || '')}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select supplier"
                        placeholder="Search supplier"
                        error={supplierFieldError}
                        helperText={supplierFieldError ? 'Supplier is required' : ''}
                      />
                    )}
                  />
                  )}
                </Box>
                <Tooltip title="Purchase for" arrow placement="top">
                  <Box sx={{ width: '100%', minWidth: 0, flex: { xs: 'none', md: '1 1 0%' } }}>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      color="standard"
                      value={formData.purchase_for}
                      onChange={(_event, newValue) => {
                        if (newValue !== null && !isReadOnly && !isFormLoading) {
                          handleFormChange('purchase_for', newValue);
                        }
                      }}
                      disabled={isReadOnly || isFormLoading}
                      aria-label="Purchase for hotel or restaurant"
                      sx={segmentedToggleGroupFullWidthSx}
                    >
                      <ToggleButton value="hotel" aria-label="Hotel">
                        <HotelOutlinedIcon />
                        Hotel
                      </ToggleButton>
                      <ToggleButton value="restaurant" aria-label="Restaurant">
                        <RestaurantMenuIcon />
                        Restaurant
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Tooltip>
              </Box>
              {!isReadOnly && canManageAiInventory && (
                <AiGradientButton
                  onClick={() => setInvoiceUploadOpen(true)}
                  disabled={isFormLoading}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    alignSelf: { xs: 'stretch', md: 'center' },
                    flexShrink: 0,
                    minWidth: 168,
                  }}
                >
                  AI Upload Invoice
                </AiGradientButton>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: sectionBg }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1.25, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? '#ddd' : '#222' }}>
                  Raw Material Items
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#9aa0a6' : '#6b7280' }}>
                  Invoice-style row entry with quick actions and inline tax columns.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {!isReadOnly && (
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    variant="outlined"
                    size="small"
                    onClick={addItemRow}
                    sx={{ textTransform: 'none' }}
                  >
                    Add New
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isReadOnly}
                  sx={{ textTransform: 'none' }}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setMoreActionMenuAnchor(e.currentTarget)}
                >
                  More Action
                </Button>
                <Tooltip
                  title="Select rows with the checkboxes (or the header to select all), then choose a slab. Applies only to selected rows (CGST/SGST half each, IGST 0)."
                  arrow
                  placement="bottom-start"
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isReadOnly || selectedItemIds.length === 0}
                      sx={{ textTransform: 'none' }}
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={(e) => setApplyTaxMenuAnchor(e.currentTarget)}
                    >
                      Apply Tax
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

                {itemsTableError && !loadingInventory && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Each line needs a raw material selected and a quantity greater than zero.
              </Alert>
            )}

            <Box sx={{ overflowX: { xs: 'visible', md: 'auto' }, pb: 0.5 }}>
              <Box sx={{ minWidth: { xs: 'unset', md: 1180 } }}>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: RAW_ITEM_GRID_TEMPLATE,
                    gap: 1,
                    px: 1.15,
                    py: 0.55,
                    mb: 0.8,
                    borderRadius: 1.25,
                    border: `1px solid ${isDark ? '#314554' : '#dbeafe'}`,
                    backgroundColor: isDark ? 'rgba(76,225,182,0.08)' : '#f3f8ff',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkbox
                      size="small"
                      disabled={isReadOnly || loadingInventory}
                      checked={areAllRowsSelected}
                      indeterminate={hasPartialSelection}
                      onChange={(e) => handleToggleAllRows(e.target.checked)}
                    />
                  </Box>
                  {['Raw Material', 'Qty', 'Unit', 'Price', 'Amount', 'CGST %', 'SGST %', 'IGST %', 'Action'].map((heading) => (
                    <Typography
                      key={heading}
                      variant="caption"
                      sx={{ fontWeight: 700, color: isDark ? '#d1d5db' : '#334155', letterSpacing: 0.2 }}
                    >
                      {heading}
                    </Typography>
                  ))}
                </Box>

                {loadingInventory ? (
                  <StockItemsTableSkeleton isDark={isDark} rows={Math.max(2, items.length || 2)} />
                ) : (
                items.map((item, index) => {
                  const materialOptions =
                    item.product && !rawMaterialProductOptions.includes(item.product)
                      ? [item.product, ...rawMaterialProductOptions]
                      : rawMaterialProductOptions;
                  const materialErr =
                    submitAttempted && !(String(item.name || item.product || '').trim());
                  const qtyErr = submitAttempted && !(Number(item.quantity || 0) > 0);
                  return (
                  <Paper
                    key={item.id}
                    elevation={0}
                    sx={{
                      p: { xs: 1.25, md: 1 },
                      mb: 0.9,
                      borderRadius: 1.5,
                      border: `1px solid ${borderColor}`,
                      backgroundColor: itemCardBg,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        fontWeight: 700,
                        mb: 1,
                        color: isDark ? '#e5e7eb' : '#111827',
                      }}
                    >
                      Item {index + 1}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0,1fr)', md: RAW_ITEM_GRID_TEMPLATE },
                        gap: { xs: 1.25, md: 1 },
                        alignItems: { xs: 'stretch', md: 'center' },
                      }}
                    >
                      <RawMaterialField label="Select" isDark={isDark} center>
                        <Checkbox
                          size="small"
                          disabled={isReadOnly}
                          checked={selectedItemIds.includes(item.id)}
                          onChange={() => handleToggleRowSelection(item.id)}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="Raw Material" isDark={isDark}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={materialOptions}
                          value={item.product || null}
                          onChange={(_event, newValue) =>
                            handleItemChange(item.id, 'product', newValue ?? '')
                          }
                          disabled={isReadOnly || isFormLoading}
                          getOptionLabel={(option) => option}
                          isOptionEqualToValue={(option, value) => option === value}
                          filterOptions={filterRawMaterialOptions}
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                          noOptionsText="No matching raw material"
                          componentsProps={{
                            paper: {
                              sx: {
                                mt: 0.5,
                                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                                border: `1px solid ${borderColor}`,
                                boxShadow: isDark
                                  ? '0 12px 28px rgba(0,0,0,0.5)'
                                  : '0 12px 28px rgba(15,23,42,0.12)',
                                '& .MuiAutocomplete-listbox': {
                                  py: 0.5,
                                  maxHeight: 280,
                                  '& .MuiAutocomplete-option': {
                                    fontSize: '0.875rem',
                                    py: 1,
                                  },
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search raw material..."
                              error={materialErr}
                              helperText={materialErr ? 'Raw material is required' : ''}
                              InputProps={{
                                ...params.InputProps,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  minHeight: 40,
                                  paddingRight: '9px !important',
                                },
                                '& .MuiAutocomplete-input': {
                                  fontSize: '0.875rem',
                                },
                              }}
                            />
                          )}
                          sx={{ width: '100%', minWidth: 0 }}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="Qty" isDark={isDark}>
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <StockQtyStepper
                            value={item.quantity}
                            onChange={(v) => handleItemChange(item.id, 'quantity', v)}
                            disabled={isReadOnly || isFormLoading}
                            isDark={isDark}
                            borderColor={qtyErr ? theme.palette.error.main : inputOutlineColor}
                          />
                          {qtyErr && (
                            <FormHelperText error sx={{ mx: 0, mt: 0.25 }}>
                              Quantity must be greater than 0
                            </FormHelperText>
                          )}
                        </Box>
                      </RawMaterialField>
                      <RawMaterialField label="Unit" isDark={isDark}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={item.unit}
                          onChange={(e) => handleItemChange(item.id, 'unit', e.target.value)}
                          disabled={isReadOnly || isFormLoading}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {UNIT_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </RawMaterialField>
                      <RawMaterialField label="Price" isDark={isDark}>
                        <NumericFieldWithMuiSteppers
                          step={1}
                          min={0}
                          placeholder="0"
                          value={item.price}
                          onChange={(e) => handleItemChange(item.id, 'price', e.target.value)}
                          disabled={isReadOnly || isFormLoading}
                          isDark={isDark}
                          borderColor={inputOutlineColor}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="Amount" isDark={isDark}>
                        <TextField
                          fullWidth
                          size="small"
                          value={getItemAmount(item).toFixed(2)}
                          inputProps={{
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            readOnly: true,
                            sx: { pr: 0 },
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ m: 0, ml: '0 !important', mr: '0 !important', pointerEvents: 'none' }}
                              >
                                <Box
                                  aria-hidden
                                  sx={{
                                    width: 32,
                                    minWidth: 32,
                                    height: 40,
                                    flexShrink: 0,
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="CGST %" isDark={isDark}>
                        <NumericFieldWithMuiSteppers
                          step={1}
                          min={0}
                          max={100}
                          placeholder="0"
                          value={item.cgst}
                          onChange={(e) => handleItemChange(item.id, 'cgst', e.target.value)}
                          disabled={isReadOnly || isFormLoading}
                          isDark={isDark}
                          borderColor={inputOutlineColor}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="SGST %" isDark={isDark}>
                        <NumericFieldWithMuiSteppers
                          step={1}
                          min={0}
                          max={100}
                          placeholder="0"
                          value={item.sgst}
                          onChange={(e) => handleItemChange(item.id, 'sgst', e.target.value)}
                          disabled={isReadOnly || isFormLoading}
                          isDark={isDark}
                          borderColor={inputOutlineColor}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="IGST %" isDark={isDark}>
                        <NumericFieldWithMuiSteppers
                          step={1}
                          min={0}
                          max={100}
                          placeholder="0"
                          value={item.igst}
                          onChange={(e) => handleItemChange(item.id, 'igst', e.target.value)}
                          disabled={isReadOnly || isFormLoading}
                          isDark={isDark}
                          borderColor={inputOutlineColor}
                        />
                      </RawMaterialField>
                      <RawMaterialField label="Action" isDark={isDark} center>
                        {!isReadOnly ? (
                          <IconButton
                            size="small"
                            onClick={() => removeItemRow(item.id)}
                            sx={{ color: '#ef5350' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`#${index + 1}`}
                            sx={{ color: isDark ? '#cbd5e1' : '#475569', width: 'fit-content' }}
                          />
                        )}
                      </RawMaterialField>
                    </Box>
                  </Paper>
                  );
                })
                )}
              </Box>
            </Box>

            <Menu
              anchorEl={moreActionMenuAnchor}
              open={Boolean(moreActionMenuAnchor)}
              onClose={() => setMoreActionMenuAnchor(null)}
            >
              <MenuItem onClick={() => handleMoreAction('duplicate_last')}>Duplicate Last Row</MenuItem>
              <MenuItem onClick={() => handleMoreAction('fill_units')}>Fill Empty Units (kg)</MenuItem>
              <MenuItem onClick={() => handleMoreAction('reset_taxes')}>Reset All Taxes</MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMoreAction('remove_selected')}>Remove Selected Rows</MenuItem>
              <MenuItem onClick={() => handleMoreAction('clear_all_rows')}>Clear All Rows</MenuItem>
            </Menu>

            <Menu
              anchorEl={applyTaxMenuAnchor}
              open={Boolean(applyTaxMenuAnchor)}
              onClose={() => setApplyTaxMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 220 } }}
            >
              {GST_APPLY_SLABS.map((rate, index) => (
                <React.Fragment key={rate}>
                  {index > 0 && <Divider />}
                  <MenuItem
                    disabled={isFormLoading}
                    onClick={() => applyGstSlabToRows(rate)}
                    sx={{ textTransform: 'none', py: 1 }}
                  >
                    {rate === 0 ? 'Clear CGST / SGST / IGST' : `${rate}% GST`}
                  </MenuItem>
                </React.Fragment>
              ))}
            </Menu>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${borderColor}`,
              backgroundColor: sectionBg,
            }}
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    color: isDark ? '#e5e7eb' : '#111827',
                    letterSpacing: 0.02,
                  }}
                >
                  Payment details
                </Typography>
                {loadingInventory ? (
                  <StockPaymentDetailsSkeleton isDark={isDark} />
                ) : (
                <>
                <Tooltip title="Payment status" arrow placement="top">
                  <Box sx={{ width: '100%', minWidth: 0, display: 'flex', alignItems: 'stretch', mb: 2 }}>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      color="standard"
                      value={formData.payment_type}
                      onChange={(_event, newValue) => handlePaymentTypeChange(newValue)}
                      disabled={isReadOnly || isFormLoading}
                      aria-label="Paid, partial paid, or unpaid"
                      sx={segmentedToggleGroupFullWidthSx}
                    >
                      <ToggleButton value="paid" aria-label="Paid">
                        <CheckCircleOutlineIcon />
                        Paid
                      </ToggleButton>
                      <ToggleButton value="partial_paid" aria-label="Partial paid">
                        <PaymentsOutlined />
                        Partial
                      </ToggleButton>
                      <ToggleButton value="unpaid" aria-label="Unpaid">
                        <HourglassEmptyOutlinedIcon />
                        Unpaid
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Tooltip>

                {paymentSectionError && paymentValidationMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {paymentValidationMessage}
                  </Alert>
                )}

                {(formData.payment_type === 'paid' || formData.payment_type === 'partial_paid') && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontWeight: 500 }}>
                        Payments
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.7rem' }}>
                        *
                      </Typography>
                    </Box>
                    {!isFormLoading &&
                      formData.payment_type === 'paid' &&
                      paymentRowsWithAmount.length > 0 &&
                      !paidTotalsMatch &&
                      !(submitAttempted && paymentSectionError) && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.error.main, mb: 1, display: 'block', fontSize: '0.7rem' }}
                        >
                          Total paid must equal grand total ({summary.total.toFixed(2)})
                        </Typography>
                      )}
                    {!isFormLoading &&
                      formData.payment_type === 'partial_paid' &&
                      paymentRowsWithAmount.length > 0 &&
                      !partialTotalsValid &&
                      !(submitAttempted && paymentSectionError) && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.error.main, mb: 1, display: 'block', fontSize: '0.7rem' }}
                        >
                          Total paid must be greater than 0 and not exceed grand total ({summary.total.toFixed(2)})
                        </Typography>
                      )}

                    {(formData.payment_entries || []).map((row) => (
                      <Box
                        key={row.id}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                          alignItems: { xs: 'stretch', sm: 'flex-start' },
                          mb: 1.25,
                        }}
                      >
                        {isReadOnly ? (
                          <>
                            <Typography variant="body2" sx={{ flex: 1, pt: 0.5 }}>
                              {String(row.method || '—')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 100, pt: 0.5 }}>
                              {Number(row.amount || 0).toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Method"
                              value={row.method || 'CASH'}
                              onChange={(e) => updatePaymentRow(row.id, 'method', e.target.value)}
                              disabled={isFormLoading}
                              sx={{ flex: { sm: 1 }, minWidth: 0 }}
                            >
                              {STOCK_PAYMENT_METHODS.map((method) => (
                                <MenuItem key={method} value={method}>
                                  {method}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Box sx={{ width: { xs: '100%', sm: 148 }, flexShrink: 0 }}>
                              <NumericFieldWithMuiSteppers
                                step={1}
                                min={0}
                                value={row.amount}
                                onChange={(e) => updatePaymentRow(row.id, 'amount', e.target.value)}
                                disabled={isFormLoading}
                                isDark={isDark}
                                borderColor={inputOutlineColor}
                                placeholder="0"
                              />
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removePaymentRow(row.id)}
                              disabled={isFormLoading || (formData.payment_entries || []).length <= 1}
                              sx={{ color: '#ef5350', mt: { sm: 0.25 }, alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}
                              aria-label="Remove payment row"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    ))}

                    {!isReadOnly && (
                      <Button
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={addPaymentRow}
                        disabled={isFormLoading}
                        sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}
                      >
                        Add payment
                      </Button>
                    )}

                    {!isReadOnly && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isDark ? '#94a3b8' : '#64748b' }}>
                        {formData.payment_type === 'paid'
                          ? 'Split across methods; total paid must match grand total.'
                          : `Sum of amounts must be greater than 0 and at most ${summary.total.toFixed(2)}.`}
                      </Typography>
                    )}
                  </Box>
                )}

                <Grid container spacing={2} sx={{ mt: 2 }} alignItems="stretch">
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                      <Grid item xs={12} md={9}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#e5e7eb' : '#111827',
                          }}
                        >
                          Invoice document camera upload
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', md: 'flex-end' },
                            height: '100%',
                            alignItems: 'center',
                          }}
                        >
                          <Switch
                            checked={invoiceCameraUploadMode}
                            onChange={(e) => setInvoiceCameraUploadMode(e.target.checked)}
                            disabled={isReadOnly || isFormLoading}
                            color="primary"
                            inputProps={{
                              'aria-label': 'Use camera for invoice document',
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <input
                      ref={invoiceDocInputRef}
                      type="file"
                      multiple
                      accept=".pdf,application/pdf,image/jpeg,image/png,image/gif,image/webp"
                      hidden
                      onChange={handleInvoiceDocumentFile}
                    />
                    <canvas ref={invoiceCameraCanvasRef} style={{ display: 'none' }} aria-hidden />

                    <Paper
                      elevation={0}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isReadOnly || invoiceDocUploading || isFormLoading) return;
                        const files = Array.from(e.dataTransfer?.files || []);
                        for (const f of files) {
                          await uploadInvoiceMediaFile(f);
                        }
                      }}
                      sx={{
                        p: 2,
                        border: `2px dashed ${borderColor}`,
                        borderRadius: 2,
                        textAlign: 'left',
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      }}
                    >
                      {invoiceDocUploading ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          Uploading…
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {(formData.invoice_documents || []).length > 0 && (
                            <Stack spacing={1.5}>
                              {(formData.invoice_documents || []).map((doc) => {
                                const showPdf =
                                  isInvoicePdfUrl(doc.url) || invoicePreviewFailedById[doc.id];
                                return (
                                  <Box
                                    key={doc.id}
                                    sx={{
                                      borderBottom: `1px solid ${borderColor}`,
                                      pb: 1.5,
                                      '&:last-of-type': { borderBottom: 'none', pb: 0 },
                                    }}
                                  >
                                    {showPdf ? (
                                      <Box textAlign="center">
                                        <PictureAsPdfIcon
                                          sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                          {isInvoicePdfUrl(doc.url) ? 'PDF document' : 'Document'}
                                        </Typography>
                                        <Button
                                          component="a"
                                          href={doc.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          size="small"
                                          sx={{ mr: 1, textTransform: 'none' }}
                                        >
                                          View
                                        </Button>
                                        {!isReadOnly && (
                                          <Button
                                            size="small"
                                            color="error"
                                            sx={{ textTransform: 'none' }}
                                            onClick={() => removeInvoiceDocument(doc.id)}
                                          >
                                            Remove
                                          </Button>
                                        )}
                                      </Box>
                                    ) : (
                                      <Box>
                                        <Box
                                          component="img"
                                          src={doc.url}
                                          alt="Invoice"
                                          onError={() =>
                                            setInvoicePreviewFailedById((p) => ({ ...p, [doc.id]: true }))
                                          }
                                          sx={{
                                            maxWidth: '100%',
                                            maxHeight: 200,
                                            borderRadius: 1,
                                            objectFit: 'contain',
                                            display: 'block',
                                            mx: 'auto',
                                          }}
                                        />
                                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                                          <Button
                                            component="a"
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                            sx={{ mr: 1, textTransform: 'none' }}
                                          >
                                            View
                                          </Button>
                                          {!isReadOnly && (
                                            <Button
                                              size="small"
                                              color="error"
                                              sx={{ textTransform: 'none' }}
                                              onClick={() => removeInvoiceDocument(doc.id)}
                                            >
                                              Remove
                                            </Button>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}

                          {!isReadOnly && (
                            <Box
                              onClick={() => {
                                if (invoiceDocUploading || isFormLoading) return;
                                handleInvoiceDocumentZoneClick();
                              }}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                textAlign: 'center',
                                cursor: invoiceDocUploading || isFormLoading ? 'default' : 'pointer',
                                border: `1px dashed ${borderColor}`,
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                '&:hover':
                                  invoiceDocUploading || isFormLoading
                                    ? {}
                                    : {
                                        backgroundColor: isDark
                                          ? 'rgba(255, 255, 255, 0.08)'
                                          : 'rgba(0, 0, 0, 0.04)',
                                      },
                              }}
                            >
                              {invoiceCameraUploadMode ? (
                                <CameraAltIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                              ) : (
                                <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                              )}
                              <Typography variant="body2" color="text.secondary">
                                {(formData.invoice_documents || []).length > 0
                                  ? invoiceCameraUploadMode
                                    ? 'Add another — capture from camera'
                                    : 'Add another — click or drag files here'
                                  : invoiceCameraUploadMode
                                    ? 'Click to capture from camera'
                                    : 'Click to upload or drag and drop'}
                              </Typography>
                            </Box>
                          )}

                          {isReadOnly && !(formData.invoice_documents || []).length && (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              No invoice file attached
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        justifyContent: { xs: 'flex-start', md: 'center' },
                      }}
                    >
                      <DatePicker
                        label="Invoice Date"
                        value={invoiceDateValue}
                        onChange={(newDate) => {
                          if (!newDate || !newDate.isValid?.()) {
                            handleFormChange('invoice_date', '');
                            return;
                          }
                          handleFormChange('invoice_date', newDate.format(DATE_FORMAT));
                        }}
                        disabled={isReadOnly || isFormLoading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: invoiceDateFieldError,
                            helperText: invoiceDateFieldError ? 'Invoice date is required' : '',
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Invoice Number"
                        value={formData.invoice_number}
                        onChange={(e) => handleFormChange('invoice_number', e.target.value)}
                        disabled={isReadOnly || isFormLoading}
                      />
                    </Box>
                  </Grid>
                </Grid>
                </>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    color: isDark ? '#e5e7eb' : '#111827',
                    letterSpacing: 0.02,
                  }}
                >
                  Billing summary
                </Typography>
                {loadingInventory ? (
                  <StockBillingSummarySkeleton isDark={isDark} />
                ) : (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? '#3a3a4f' : '#e5e7eb'}`,
                    backgroundColor: isDark ? '#24242c' : '#fafafa',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2, pb: 1, borderBottom: `1px dashed ${borderColor}` }}>
                    <Typography sx={{ fontWeight: 700, color: isDark ? '#e5e7eb' : '#111827' }}>Sub Total</Typography>
                    <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#111827' }}>{summary.subtotal.toFixed(2)}</Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) 130px' }, gap: 1, alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#2563eb', justifySelf: 'start' }}>
                      + Total Discount
                    </Typography>
                    <StockQtyStepper
                      value={formData.discount}
                      onChange={(v) => handleFormChange('discount', v)}
                      disabled={isReadOnly || isFormLoading}
                      isDark={isDark}
                      borderColor={inputOutlineColor}
                      ariaLabel="Total discount"
                      ariaLabelIncrease="Increase discount"
                      ariaLabelDecrease="Decrease discount"
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) 130px' }, gap: 1, alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#2563eb', justifySelf: 'start' }}>
                      + Add Other Charges
                    </Typography>
                    <NumericFieldWithMuiSteppers
                      step={1}
                      min={0}
                      value={formData.delivery_charges}
                      onChange={(e) => handleFormChange('delivery_charges', e.target.value)}
                      disabled={isReadOnly || isFormLoading}
                      isDark={isDark}
                      borderColor={inputOutlineColor}
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) 130px' }, gap: 1, alignItems: 'center', mb: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#2563eb', justifySelf: 'start' }}>
                      + Other Taxes
                    </Typography>
                    <NumericFieldWithMuiSteppers
                      step={1}
                      min={0}
                      value={formData.other_taxes}
                      onChange={(e) => handleFormChange('other_taxes', e.target.value)}
                      disabled={isReadOnly || isFormLoading}
                      isDark={isDark}
                      borderColor={inputOutlineColor}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: isDark ? '#9aa0a6' : '#6b7280', mb: 0.7 }}>
                    <Typography sx={{ fontSize: '0.83rem' }}>Item Tax</Typography>
                    <Typography sx={{ fontSize: '0.83rem', fontWeight: 600 }}>{summary.itemTax.toFixed(2)}</Typography>
                  </Box>
                  {(summary.invoiceLevelTaxRate > 0 || summary.invoiceLevelTaxAmount !== 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', color: isDark ? '#9aa0a6' : '#6b7280', mb: 1.2 }}>
                      <Typography sx={{ fontSize: '0.83rem' }}>Invoice Level GST ({summary.invoiceLevelTaxRate}%)</Typography>
                      <Typography sx={{ fontSize: '0.83rem', fontWeight: 600 }}>{summary.invoiceLevelTaxAmount.toFixed(2)}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ mb: 1.2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#111827', fontSize: '0.98rem' }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#111827', fontSize: '1.1rem' }}>
                      {summary.total.toFixed(2)}
                    </Typography>
                  </Box>

                  {(formData.payment_type === 'paid' || formData.payment_type === 'partial_paid') && (
                    <>
                      <Divider sx={{ my: 1.2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.85 }}>
                        <Typography sx={{ fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.92rem' }}>
                          Total paid
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: isDark ? '#e5e7eb' : '#111827', fontSize: '0.95rem' }}>
                          {totalPaidFromEntries.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 800, color: isDark ? '#e5e7eb' : '#111827', fontSize: '0.95rem' }}>
                          Amount due
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '1rem',
                            color:
                              amountDueFromPayments > 0
                                ? theme.palette.warning.main
                                : isDark
                                  ? '#4ade80'
                                  : '#15803d',
                          }}
                        >
                          {amountDueFromPayments.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                )}
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1.5}
              sx={{
                mt: 2.5,
                pt: 1.25,
                borderTop: `1px solid ${borderColor}`,
              }}
            >
              <Grid item xs={12} sm={isReadOnly ? 12 : 6}>
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  onClick={onCancel}
                  sx={{
                    px: 2.25,
                    py: 1,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    textTransform: 'none',
                  }}
                >
                  {isReadOnly ? 'Back' : 'Cancel'}
                </Button>
              </Grid>
              {!isReadOnly && (
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    size="medium"
                    fullWidth
                    startIcon={<SaveIcon sx={{ fontSize: 20, color: '#000' }} />}
                    onClick={handleSubmit}
                    disabled={saving || isFormLoading}
                    sx={{
                      px: 2.25,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      backgroundColor: '#4ce1b6',
                      color: '#000',
                      '&:hover': { backgroundColor: '#4ce1b6', opacity: 0.9 },
                      '& .MuiButton-startIcon': {
                        color: '#000',
                      },
                      '& .MuiButton-startIcon .MuiSvgIcon-root': {
                        color: '#000',
                      },
                    }}
                  >
                    {saving ? 'Saving…' : mode === 'edit' ? 'Update Stock' : 'Add Stock'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={invoiceCameraOpen}
        onClose={stopInvoiceCamera}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Capture invoice</Typography>
          <IconButton onClick={stopInvoiceCamera} size="small" aria-label="Close camera">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', backgroundColor: '#000', borderRadius: 1, overflow: 'hidden' }}>
            <video
              ref={invoiceCameraVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={stopInvoiceCamera} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={captureInvoiceDocument}
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            disabled={invoiceDocUploading}
            sx={{
              backgroundColor: '#4ce1b6',
              color: '#000',
              '&:hover': {
                backgroundColor: '#3ab896',
              },
            }}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {canManageAiInventory && (
        <StockAiInvoiceOverlay
          open={invoiceUploadOpen}
          onClose={() => setInvoiceUploadOpen(false)}
          onSuccess={handleApplyExtractedInvoice}
          isDark={isDark}
        />
      )}
      </Box>
    </LocalizationProvider>
  );
};

export default StockAddInventorySection;
